/* eslint-disable react/no-array-index-key */
import {
  faArrowDown,
  faArrowUp,
  faGripVertical,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  Cell,
  Header,
  Row,
  RowData,
  Table as TableType,
} from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { memo } from 'react';

import ColumnsVisibilityAndOrdering from './ColumnsVisibilityAndOrdering';
import { Skeleton } from './ui/skeleton';

const theadStyle = cva('border-b transition-colors', {
  variants: {
    variant: {
      dark: 'border-neutral-600 bg-neutral-1000',
      light: 'border-jb-neutral-500 bg-jb-neutral-100',
    },
  },
  defaultVariants: {
    variant: 'dark',
  },
});
type TheadStyle = VariantProps<typeof theadStyle>;

const thTableStyle = cva('relative text-left', {
  variants: {
    variant: {
      dark: classNames('group border-neutral-600 p-3'),
      light: classNames(
        'group border-jb-neutral-500 p-4 text-jb-body-medium font-semibold text-jb-neutral-1000'
      ),
    },
  },
  defaultVariants: {
    variant: 'dark',
  },
});
export type ThTableStyle = VariantProps<typeof thTableStyle>;

const resizeHandleStyle = cva(
  'absolute right-0 top-0 h-full w-5 hover:cursor-col-resize',
  {
    variants: {
      variant: {
        dark: 'group-hover:bg-neutral-800',
        light: 'group-hover:bg-jb-neutral-300',
      },
    },
    defaultVariants: {
      variant: 'dark',
    },
  }
);
type ResizeHandleStyle = VariantProps<typeof resizeHandleStyle>;

const tbodyStyle = cva('divide-y overflow-auto', {
  variants: {
    variant: {
      dark: 'divide-neutral-600',
      light: 'divide-jb-neutral-500',
    },
  },
  defaultVariants: {
    variant: 'dark',
  },
});
type TbodyStyle = VariantProps<typeof tbodyStyle>;

const trStyle = (isOdd: boolean) =>
  cva('bg-transparent transition-colors', {
    variants: {
      variant: {
        dark: classNames('hover:!bg-purple-500/20', {
          '!bg-neutral-1000/50': isOdd,
        }),
        light: classNames('hover:!bg-jb-primary-200', {
          '!bg-jb-primary-100/90': isOdd,
        }),
      },
    },
    defaultVariants: {
      variant: 'dark',
    },
  });
type TrStyle = VariantProps<ReturnType<typeof trStyle>>;

const tdStyle = cva('relative h-px', {
  variants: {
    variant: {
      dark: 'border-neutral-600 px-3 py-2',
      light:
        'border-jb-neutral-500 px-4 py-3 text-jb-body-medium text-jb-neutral-1000',
    },
  },
  defaultVariants: {
    variant: 'dark',
  },
});
type TdStyle = VariantProps<typeof tdStyle>;

export interface TableVariantProps
  extends TheadStyle,
    ThTableStyle,
    ResizeHandleStyle,
    TbodyStyle,
    TrStyle,
    TdStyle {}

type Props<T extends RowData> = TableVariantProps & {
  table: TableType<T>;
  headerPrefix?: (header: Header<T, unknown>) => JSX.Element | undefined | null;
  headerSuffix?: (header: Header<T, unknown>) => JSX.Element | undefined | null;
  tableHeadClassName?: string;
  tableHeaderClassName?: (header: Header<T, unknown>) => string;
  visibilityAndOrderingProps?: Omit<
    ComponentProps<typeof ColumnsVisibilityAndOrdering>,
    'table'
  >;
  tableHeaderLabelContainerClassName?: (header: Header<T, unknown>) => string;
  tableCellClassName?: (
    isDummyData: boolean,
    cell?: Cell<T, unknown>
  ) => string;
  tableHeaderWrapClassName?: (header: Header<T, unknown>) => string;
  hideVisibilityAndOrdering?: boolean;
  size?: 'manual' | 'auto';
  onRowClick?: (row: Row<T>) => void;
  showDummyData?: boolean;
  dummyDataLength?: number;
  showSkeleton?: boolean;
};
const Table = <T extends RowData>({
  table,
  headerPrefix,
  headerSuffix,
  tableHeadClassName,
  visibilityAndOrderingProps,
  hideVisibilityAndOrdering = false,
  tableHeaderClassName,
  tableCellClassName,
  tableHeaderLabelContainerClassName,
  size = 'auto',
  onRowClick,
  dummyDataLength = 10,
  showDummyData = false,
  showSkeleton = false,
  tableHeaderWrapClassName,
  variant,
}: Props<T>) => {
  const shouldUseTableSize = size === 'manual';
  return (
    <table
      style={{ width: shouldUseTableSize ? table.getTotalSize() : undefined }}
      className="mb-3 min-w-full border-separate border-spacing-0 overflow-auto rounded-lg [&_th:first-child]:w-[20px] [&_th:first-of-type]:rounded-tl-lg [&_th:last-child]:w-[20px] [&_th:last-of-type]:rounded-tr-lg [&_tr:last-of-type_td:first-of-type]:rounded-bl-lg [&_tr:last-of-type_td:last-of-type]:rounded-br-lg [&_tr_td:first-child]:border-l [&_tr_td:last-child]:border-r [&_tr_td]:border-b [&_tr_th:first-child]:border-l [&_tr_th:last-child]:border-r [&_tr_th]:border-y [&tr_th]:border-t"
    >
      <thead
        className={classNames(theadStyle({ variant }), tableHeadClassName)}
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                className={classNames(
                  thTableStyle({ variant }),
                  tableHeaderClassName?.(header),
                  {
                    'bg-primary-500/50': header.column.getIsResizing(),
                  }
                )}
                key={header.id}
                style={{
                  width: shouldUseTableSize ? header.getSize() : undefined,
                }}
              >
                {header.column.getCanResize() && (
                  <div
                    className={classNames(resizeHandleStyle({ variant }), {
                      '!bg-primary-300': header.column.getIsResizing(),
                      'cursor-col-resize': header.column.getIsResizing(),
                    })}
                    onClickCapture={(e) => e.stopPropagation()}
                    onMouseDownCapture={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onDoubleClick={header.column.resetSize}
                  />
                )}
                <div
                  className={classNames(
                    'flex items-center gap-2',
                    tableHeaderWrapClassName?.(header)
                  )}
                >
                  {!hideVisibilityAndOrdering && (
                    <ColumnsVisibilityAndOrdering
                      {...visibilityAndOrderingProps}
                      table={table}
                    >
                      <FontAwesomeIcon
                        icon={faGripVertical}
                        size="xs"
                        className="!hidden  group-hover:!block"
                      />
                    </ColumnsVisibilityAndOrdering>
                  )}
                  {headerPrefix?.(header)}
                  <div
                    role="presentation"
                    className={classNames(
                      'flex w-fit items-center gap-1',
                      {
                        'cursor-pointer': header.column.getCanSort(),
                      },
                      tableHeaderLabelContainerClassName?.(header)
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: <FontAwesomeIcon icon={faArrowUp} />,
                      desc: <FontAwesomeIcon icon={faArrowDown} />,
                    }[header.column.getIsSorted() as string] ?? null}
                  </div>
                  {headerSuffix?.(header)}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className={tbodyStyle({ variant })}>
        {showDummyData &&
          Array.from({ length: dummyDataLength }).map((_, index) => (
            <tr key={index} className={trStyle(index % 2 !== 0)({ variant })}>
              {table.getVisibleLeafColumns().map((__, tdIndex) => (
                <td
                  className={classNames(
                    tdStyle({ variant }),
                    tableCellClassName?.(true, undefined)
                  )}
                  key={tdIndex}
                >
                  <Skeleton className="h-[42px] rounded-none" />
                </td>
              ))}
            </tr>
          ))}
        {table.getRowModel().rows.map((row, index) => (
          <RowView
            key={row.id}
            row={row}
            index={index}
            variant={variant}
            isSelected={row.getIsSelected()}
            showSkeleton={showSkeleton}
            shouldUseTableSize={shouldUseTableSize}
            onRowClick={onRowClick}
            tableCellClassName={tableCellClassName}
          />
        ))}
      </tbody>
    </table>
  );
};

export default Table;

type RowViewProps<T extends RowData> = TableVariantProps & {
  row: Row<T>;
  index: number;
  isSelected: boolean;
  showSkeleton: boolean;
  shouldUseTableSize: boolean;
  onRowClick?: (row: Row<T>) => void;
  tableCellClassName?: (
    isDummyData: boolean,
    cell?: Cell<T, unknown>
  ) => string;
};

const RowViewInner = <T extends RowData>({
  row,
  index,
  variant,
  isSelected: _isSelected,
  showSkeleton,
  shouldUseTableSize,
  onRowClick,
  tableCellClassName,
}: RowViewProps<T>) => (
  <tr
    onClick={() => onRowClick?.(row)}
    className={classNames(trStyle(index % 2 !== 0)({ variant }), {
      'cursor-pointer': !!onRowClick,
    })}
  >
    {row.getVisibleCells().map((cell) => (
      <td
        className={classNames(
          tdStyle({ variant }),
          tableCellClassName?.(false, cell)
        )}
        key={cell.id}
        style={{
          width: shouldUseTableSize ? cell.column.getSize() : undefined,
        }}
      >
        {showSkeleton && (
          <Skeleton className="absolute left-0 top-0 z-50 h-full w-full rounded-none opacity-60 transition-all" />
        )}
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </td>
    ))}
  </tr>
);
const RowView = memo(RowViewInner) as typeof RowViewInner;
